import React, { useCallback, useEffect, useState } from "react";

import { OpportunityFormModal } from "../components/OpportunityFormModal";
import { RescheduleModal } from "../components/RescheduleModal";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import {
  Channel,
  Company,
  Opportunity,
  Pipeline,
  Product,
  TodayItem,
  TodayResponse,
  UserMini,
} from "../types/crm";

interface SectionConfig {
  key: keyof Omit<TodayResponse, "stale_lead_days">;
  title: string;
  accent: string; // classe da barra/realce da seção
  badge: string; // classe do badge do motivo
}

const SECTIONS: SectionConfig[] = [
  {
    key: "fups_vencidos",
    title: "FUPs vencidos",
    accent: "border-l-red-500",
    badge: "bg-red-50 text-red-600",
  },
  {
    key: "fups_hoje",
    title: "FUPs de hoje",
    accent: "border-l-primary",
    badge: "bg-primary-soft text-primary",
  },
  {
    key: "leads_parados",
    title: "Leads parados",
    accent: "border-l-amber-400",
    badge: "bg-amber-100 text-amber-700",
  },
  {
    key: "aprovacao_travada",
    title: "Travados em aprovação interna",
    accent: "border-l-secondary",
    badge: "bg-primary-soft text-primary",
  },
];

export function TodayPage() {
  const [data, setData] = useState<TodayResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Dados de referência para o modal completo (aberto via "Abrir").
  const [companies, setCompanies] = useState<Company[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [users, setUsers] = useState<UserMini[]>([]);

  const [rescheduling, setRescheduling] = useState<TodayItem | null>(null);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [openingId, setOpeningId] = useState<number | null>(null);

  const fetchToday = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<TodayResponse>("/today/");
      setData(response.data);
    } catch {
      setError("Não foi possível carregar as tarefas de hoje.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  useEffect(() => {
    Promise.all([
      api.get<Company[]>("/companies/"),
      api.get<Channel[]>("/channels/"),
      api.get<Product[]>("/products/"),
      api.get<Pipeline[]>("/pipelines/"),
      api.get<UserMini[]>("/users/"),
    ])
      .then(([companiesRes, channelsRes, productsRes, pipelinesRes, usersRes]) => {
        setCompanies(companiesRes.data);
        setChannels(channelsRes.data);
        setProducts(productsRes.data);
        setPipelines(pipelinesRes.data);
        setUsers(usersRes.data.filter((user) => user.is_active));
      })
      .catch(() => {
        /* modal completo simplesmente não abrirá; ações principais seguem */
      });
  }, []);

  const handleRegistrarContato = async (item: TodayItem) => {
    if (!item.followup_id) return;
    setActionError(null);
    try {
      await api.post(`/followups/${item.followup_id}/concluir/`);
      await fetchToday();
    } catch {
      setActionError("Não foi possível registrar o contato.");
    }
  };

  const handleAbrir = async (item: TodayItem) => {
    setActionError(null);
    setOpeningId(item.opportunity_id);
    try {
      const response = await api.get<Opportunity>(`/opportunities/${item.opportunity_id}/`);
      setEditing(response.data);
    } catch {
      setActionError("Não foi possível abrir a oportunidade.");
    } finally {
      setOpeningId(null);
    }
  };

  const totalItems = data
    ? data.fups_vencidos.length +
      data.fups_hoje.length +
      data.leads_parados.length +
      data.aprovacao_travada.length
    : 0;

  return (
    <div className="p-8">
      <h1 className="text-xl font-semibold text-slate-800">O que fazer hoje</h1>
      <p className="mt-1 text-sm text-slate-500">
        Suas prioridades do momento, em ordem de urgência.
      </p>

      {isLoading && <p className="mt-6 text-sm text-slate-500">Carregando...</p>}

      {!isLoading && error && (
        <div className="mt-6 rounded-[10px] border border-border-subtle bg-surface p-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={fetchToday}
            className="mt-3 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {actionError && <p className="mt-4 text-sm text-red-600">{actionError}</p>}

      {!isLoading && !error && data && totalItems === 0 && (
        <div className="mt-6 rounded-[10px] border border-border-subtle bg-surface p-8 text-center">
          <p className="text-sm text-slate-600">
            Tudo em dia! Nenhuma ação pendente no momento.
          </p>
        </div>
      )}

      {!isLoading && !error && data && totalItems > 0 && (
        <div className="mt-6 space-y-8">
          {SECTIONS.map((section) => {
            const items = data[section.key];
            if (items.length === 0) return null;
            return (
              <section key={section.key}>
                <h2 className="text-sm font-semibold text-slate-700">
                  {section.title}{" "}
                  <span className="text-slate-400">({items.length})</span>
                </h2>
                <div className="mt-3 space-y-2">
                  {items.map((item) => (
                    <div
                      key={`${section.key}-${item.opportunity_id}-${item.followup_id ?? "x"}`}
                      className={`rounded-[10px] border border-border-subtle border-l-4 bg-white p-4 ${section.accent}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-slate-800">
                              {item.company_name}
                            </p>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${section.badge}`}
                            >
                              {item.reason}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">
                            {item.stage_name} · {item.owner_username}
                            {item.due_date ? ` · vence ${formatDate(item.due_date)}` : ""}
                          </p>
                          {item.suggested_action && (
                            <p className="mt-2 text-sm text-slate-600">
                              <span className="font-medium text-slate-700">Sugestão: </span>
                              {item.suggested_action}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-shrink-0 flex-wrap gap-2">
                          {item.followup_id && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleRegistrarContato(item)}
                                className="rounded-[10px] bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-dark"
                              >
                                Registrar contato
                              </button>
                              <button
                                type="button"
                                onClick={() => setRescheduling(item)}
                                className="rounded-[10px] border border-border-subtle px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-surface"
                              >
                                Reagendar
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => handleAbrir(item)}
                            disabled={openingId === item.opportunity_id}
                            className="rounded-[10px] border border-border-subtle px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-surface disabled:opacity-60"
                          >
                            {openingId === item.opportunity_id ? "Abrindo..." : "Abrir"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {rescheduling && rescheduling.followup_id && (
        <RescheduleModal
          companyName={rescheduling.company_name}
          currentDueDate={rescheduling.due_date ?? ""}
          onCancel={() => setRescheduling(null)}
          onConfirm={async (dueDate) => {
            await api.post(`/followups/${rescheduling.followup_id}/reagendar/`, {
              due_date: dueDate,
            });
            setRescheduling(null);
            await fetchToday();
          }}
        />
      )}

      {editing && (
        <OpportunityFormModal
          opportunity={editing}
          companies={companies}
          channels={channels}
          products={products}
          pipelines={pipelines}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await fetchToday();
          }}
        />
      )}
    </div>
  );
}
